import {
  CardVariant,
  COLORS,
  KeycloakContext,
  LBTButton,
  LBTButtonIllustrative,
  LBTCard,
  LBTCheckbox,
  LBTChip,
  LBTImage,
  LBTLabel,
  LBTPagination,
  LBTSpacer,
  Section,
} from '@laborability/components';
import {
  Misura,
  QUESTIONARY_STEPS,
  SWIPE_DIRECTION,
  categoriesState,
  currentCompanyConfigState,
  currentUserState,
  getAllTagsCallback,
  getCategoriesHomePageCallback,
  getMisureMostPopularCallback,
  getTagBySlug,
  getUserProfileCallback,
  measuresState,
  postUserSaveFirstVisitCallback,
  resumeQuestionary,
  slugify,
  tagsState,
  useBreakpoint,
  useSwipeHook,
  useTrackAnalytics,
  handleBSolutionsWebSSORedirectCallback,
} from '@laborability/commons';
import { Box, BoxProps, Grid, Modal, Paper, Stack } from '@mui/material';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { APP_ROUTES } from '../Routes';
import BoldSpan from '../components/answerBoxes/BoldSpan';
import ElsaLogo from '../assets/images/elsa.svg';
import PromoPriveLogo from '../assets/images/promoprive.svg';

const StyledImageContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledImage = styled.img<{ maxwidth: string }>`
  width: 100%;
  max-width: ${props => props.maxwidth};
`;
export const Image = ({ src }: { src: string }) => {
  return (
    <StyledImageContainer>
      <StyledImage src={src} maxwidth={'100%'} />
    </StyledImageContainer>
  );
};

const StyledBonusBoxContainer = styled(Box)<BoxProps & { maxwidth: string }>(
  ({ maxwidth }) => ({
    width: '100%',
    maxWidth: maxwidth,
  }),
);

const StyledSituationsBoxContainer = styled(Box)<
  BoxProps & { maxwidth: string }
>(({ maxwidth }) => ({
  width: '100%',
  maxWidth: maxwidth,
}));

const SectionExplore = () => {
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const categories = useRecoilValue(categoriesState);

  return (
    <Section backgroundColor={COLORS.getInstance().WHITE}>
      <LBTSpacer spacing={4} />
      <LBTLabel variant="delaDisplay" component="h3">
        Esplora i bonus
      </LBTLabel>
      <LBTSpacer spacing={2} />
      <LBTLabel variant="spGroteskSubtitle">
        Bonus e incentivi sono organizzati in aree tematiche per aiutarti a
        navigare secondo i tuoi interessi
      </LBTLabel>
      <LBTSpacer spacing={4} />
      <Grid
        container
        spacing={isDesktop ? '24px' : '16px'}
        sx={{ maxWidth: isDesktop ? '1032px' : '390px', width: '100%' }}
      >
        {categories.map((category, index) => (
          <Grid item key={index} mobile={6} desktop={4}>
            <LBTButtonIllustrative
              datatestid={'home_button_cat_' + slugify(category.name!)}
              onClick={() => navigate(`/${APP_ROUTES.CATEGORY}/${category.id}`)}
              icon={category.icon as React.ReactNode}
              label={category.name as string}
            />
          </Grid>
        ))}
      </Grid>
      <LBTSpacer spacing={4} />
    </Section>
  );
};

export const SectionSituations = () => {
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const tags = useRecoilValue(tagsState);

  return (
    <Section backgroundColor={COLORS.getInstance().PRIMARY_MAIN}>
      <LBTSpacer spacing={4} />
      <LBTLabel variant="bodyText">
        Alcune situazioni sono trasversali alle aree tematiche. Le abbiamo
        raccolte per aiutarti a esplorare i bonus secondo le tue necessità
      </LBTLabel>
      <LBTSpacer spacing={2} />
      <StyledSituationsBoxContainer maxwidth={'1032px'}>
        <Stack
          spacing={'4px'}
          direction={'row'}
          alignItems={'center'}
          justifyContent={'center'}
          flexWrap={'wrap'}
        >
          {tags.map(tag => (
            <Box
              key={tag.id}
              component="div"
              sx={{ marginTop: '4px !important' }}
            >
              <LBTButton
                sx={{ maxHeight: isDesktop ? '47px' : '35px' }}
                datatestid={
                  'home_btn_tag_' +
                  tag.name
                    ?.split(' ')
                    .join('-')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase()
                }
                variant="contained"
                color="primary-negative"
                size={'medium'}
                onClick={() => navigate(`/${APP_ROUTES.TAG}/${tag.id}`)}
              >
                {tag.name}
              </LBTButton>
            </Box>
          ))}
        </Stack>
      </StyledSituationsBoxContainer>

      <LBTSpacer spacing={4} />
    </Section>
  );
};

const SectionBonus = () => {
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const measures = useRecoilValue(measuresState);
  const [page, setPage] = React.useState(1);

  const swipeContainerRef = useRef<HTMLDivElement>(null);
  const onSwipe = useCallback((direction: SWIPE_DIRECTION, tmpPage: number) => {
    setPage(tmpPage);
  }, []);
  useSwipeHook(swipeContainerRef, onSwipe, measures.length, page);

  const getCardCategory = (misura: Misura) => {
    if (misura.categories?.length === 0) return '-';
    if (misura.categories?.length === 2)
      return (
        misura.categories.find(category => category.parent_id)?.name ?? '-'
      );
    return misura.categories?.[0]?.name ?? '-';
  };

  return (
    <Section backgroundColor={COLORS.getInstance().PRIMARY_IPERLIGHT}>
      <LBTSpacer spacing={4} />
      <LBTLabel variant="delaDisplay" content="h3" component="h3">
        Bonus più popolari
      </LBTLabel>
      <LBTSpacer spacing={2} />
      <LBTLabel variant="spGroteskSubtitle">
        Naviga tra incentivi, agevolazioni e bonus più richiesti
      </LBTLabel>
      <LBTSpacer spacing={4} />
      <StyledBonusBoxContainer
        ref={swipeContainerRef}
        maxwidth={isDesktop ? '1032px' : '390px'}
      >
        <Grid
          container
          spacing={isDesktop ? '24px' : '16px'}
          sx={{
            maxWidth: isDesktop ? '1032px' : undefined,
            justifyContent: 'center',
          }}
        >
          {measures.map((misura: Misura, index: number) => {
            if (!isDesktop && page !== index + 1) return null;
            return (
              <Grid item mobile={12} desktop={6} key={index} display="flex">
                <LBTCard
                  onClick={() =>
                    navigate(`/${APP_ROUTES.MEASURE_DETAIL}/${misura.id}`)
                  }
                  key={index}
                  title={misura.name}
                  description={misura.description}
                  tag={getCardCategory(misura).toLowerCase()}
                  news={misura.news ?? false}
                  expired={misura.expired ?? false}
                  expiringSoon={misura.expiring_soon ?? false}
                  cardVariant={CardVariant.WHITE}
                  upcoming={misura.upcoming ?? false}
                />
              </Grid>
            );
          })}
        </Grid>

        {!isDesktop && (
          <LBTPagination
            numberOfItems={measures.length}
            onPageChange={tmpPage => setPage(tmpPage)}
            currentPage={page}
          />
        )}
      </StyledBonusBoxContainer>
      <LBTSpacer spacing={isDesktop ? 8 : 4} isFixed />
    </Section>
  );
};

const Home = () => {
  const { hasQuestionaryAccess } = useContext(KeycloakContext);
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const [isContractAccepted, setIsContractAccepted] = React.useState(false);
  const {
    tfr,
    exenergy,
    weltis,
    promoprive,
    bsolutionsweb,
    fifth_assignment,
    serenis,
    terms_and_conditions,
  } = useRecoilValue(currentCompanyConfigState);
  const getAllCategories = useRecoilCallback(getCategoriesHomePageCallback, []);
  const getAllTags = useRecoilCallback(getAllTagsCallback, []);
  const getMeasures = useRecoilCallback(getMisureMostPopularCallback, []);
  const getUserMe = useRecoilCallback(getUserProfileCallback, []);
  const currentUser = useRecoilValue(currentUserState);
  const setFirstVisitTrue = useRecoilCallback(
    postUserSaveFirstVisitCallback,
    [],
  );

  const allQuestionariesCompleted = () => {
    if (
      currentUser.questionary_status?.length === QUESTIONARY_STEPS &&
      currentUser.questionary_status?.every(
        questionary => questionary.completed,
      ) &&
      currentUser.questionary_status?.every(questionary => questionary.valid)
    )
      return true;
    return false;
  };

  const closeFirstVisitModal = async () => {
    await setFirstVisitTrue();
    getUserMe();
  };

  useEffect(() => {
    getAllCategories();
    getAllTags({ visible_only: true });
    getMeasures({ limit: 4, published: true });
    getUserMe();
    // eslint-disable-next-line
  }, [isDesktop]);

  useTrackAnalytics([
    { key: 'event', value: 'page_view' },
    { key: 'page_title', value: 'Homepage' },
    { key: 'page_location', value: '/app/homepage' },
  ]);

  // Gestione colori alternati per i moduli extra
  const SECTION_COLORS = [
    COLORS.getInstance().WHITE,
    COLORS.getInstance().PRIMARY_IPERLIGHT,
  ];
  let visibleIndex = -1;
  const getBgColor = () => {
    return SECTION_COLORS[++visibleIndex % SECTION_COLORS.length];
  };

  // BSolutionsWeb SSO callback handler
  const handleBSolutionsWebSSOClick = useRecoilCallback(handleBSolutionsWebSSORedirectCallback, []);
  const CONTRACT_PDF_URL = '/documents/Termini-e-condizioni-di-utilizzo-della-Piattaforma-Web-2026.pdf';

  return (
    <>
      <Section backgroundColor={COLORS.getInstance().PRIMARY_MAIN}>
        <LBTSpacer spacing={2} />
        <LBTLabel variant="delaDisplay" component="h1">
          Centinaia di bonus e agevolazioni per te e i tuoi cari
        </LBTLabel>
        <LBTSpacer spacing={2} />
        <LBTImage image="telescope" />
        <LBTSpacer spacing={2} />
        <LBTLabel variant="spGroteskSubtitle" component="h2">
          Avresti mai pensato che una famiglia di medio reddito può ottenere
          ogni anno in media 980 euro tra bonus e incentivi pubblici? Scopri
          quali puoi ottenere
        </LBTLabel>
        {hasQuestionaryAccess && (
          <>
            <LBTSpacer spacing={2} />
            <LBTButton
              onClick={() => {
                if (allQuestionariesCompleted())
                  navigate(`/${APP_ROUTES.MY_BONUS}`);
                else resumeQuestionary(currentUser, navigate);
              }}
              variant="contained"
              color="primary-negative"
              datatestid="home_button_sblocca_agevolazioni_1"
              sx={{ width: '100%', maxWidth: '358px' }}
            >
              {allQuestionariesCompleted()
                ? 'Vai alle tue agevolazioni'
                : 'Sblocca i tuoi bonus'}
            </LBTButton>
          </>
        )}
        <LBTSpacer spacing={4} />
      </Section>
      <SectionExplore />
      <SectionSituations />
      <SectionBonus />
      {fifth_assignment && (
        <Section backgroundColor={getBgColor()}>
          <LBTSpacer spacing={4} />
          <LBTLabel variant="delaDisplay" component="h3">
            Conosci le agevolazioni degli Enti Bilaterali?
          </LBTLabel>
          <LBTSpacer spacing={4} isFixed />
          <LBTLabel variant="spGroteskSubtitle">
            Scopri i vantaggi economici e di supporto alla persona messi a
            disposizione dalla tua azienda per te. Sono erogati tramite gli Enti
            Bilaterali, strumenti che la tua azienda ha scelto per offrirti un
            aiuto concreto.
          </LBTLabel>
          <LBTSpacer spacing={2} />
          <LBTImage image="mountainClimb" />
          <LBTSpacer spacing={4} isFixed />
          <LBTButton
            datatestid="home_button_cqs"
            variant="contained"
            color="primary-negative"
            size="medium"
            onClick={async () => {
              const res = await getTagBySlug({ slug: 'enti_bilaterali' });
              if (res?.data?.id)
                navigate(
                  `/${APP_ROUTES.TAG}/${APP_ROUTES.ENTI_BILATERALI}/${res.data.id}`,
                );
            }}
          >
            Scopri le agevolazioni
          </LBTButton>
          <LBTSpacer spacing={6} />
        </Section>
      )}
      
      {promoprive && (
        <Section backgroundColor={getBgColor()}>
          <LBTSpacer spacing={4} />
          <LBTLabel variant="delaDisplay" component="h3">
            I bonus sono un inizio. Perché fermarsi?
          </LBTLabel>
          <LBTSpacer spacing={4} isFixed />
          <LBTLabel variant="spGroteskSubtitle">
           Cashback sugli acquisti, gift card a prezzi speciali e offerte esclusive: tutto pensato per farti risparmiare ogni giorno.
          </LBTLabel>
          <LBTImage image="addToCart2" />
          <LBTSpacer spacing={2} isFixed />
          <LBTLabel variant="bodyText">
            Powered By
          </LBTLabel>
          <Box width="300px">
            <Image src={PromoPriveLogo} />
          </Box>
          <LBTSpacer spacing={4} isFixed />
          <LBTButton
            datatestid="home_button_promoprive"
            variant="contained"
            color='primary-negative'
            size="medium"
            onClick={() => window.open(`/#/${APP_ROUTES.PROMO_PRIVE}`, '_blank')}
          >
            Scopri i vantaggi
          </LBTButton>
          <LBTSpacer spacing={6} />
        </Section>
      )}

      {exenergy && (
        <Section backgroundColor={getBgColor()}>
          <LBTSpacer spacing={4} />
          <LBTLabel variant="delaDisplay" component="h3">
            Energy Management con Scouter
          </LBTLabel>
          <LBTSpacer spacing={4} isFixed />
          <LBTLabel variant="spGroteskSubtitle">
            La maggior parte delle famiglie italiane sta pagando più del necessario per luce e gas, non per disattenzione, ma perché il mercato è opaco, i contratti si rinnovano in silenzio e nessuno segnala quando è il momento di controllare.
          </LBTLabel>
          <LBTImage image="financialLiteracy3" />
          <LBTLabel variant="spGroteskSubtitle">
            Scouter rende visibile un risparmio che c'è già, e aspetta solo di essere reclamato.
          </LBTLabel>

          <LBTSpacer spacing={4} isFixed />
          <LBTButton
            datatestid="home_button_exenergy"
            variant="contained"
            color="primary-negative"
            size="medium"
            onClick={() => navigate(`/${APP_ROUTES.EXENERGY_SCOUTER}`)}
          >
            Prenota una sessione Scouter
          </LBTButton>
          <LBTSpacer spacing={6} />
        </Section>
      )}

      {serenis && (
        <Section backgroundColor={getBgColor()}>
          <LBTSpacer spacing={4} />
          <LBTLabel variant="delaDisplay" component="h3">
            Un nuovo modo di prenderti cura di te
          </LBTLabel>
          <LBTSpacer spacing={4} isFixed />
          <LBTLabel variant="spGroteskSubtitle">
            Serenis è il centro medico online scelto da migliaia di aziende italiane per il benessere dei propri dipendenti. Oggi conta oltre 3.000 professionisti selezionati - psicoterapeuti, medici, nutrizionisti, dietologi, coach, sessuologi - verificati su titolo di studio, iscrizione all'Albo ed esperienza clinica.
          </LBTLabel>
          <LBTImage image="therapyCounseling" />
          <LBTLabel variant="spGroteskSubtitle">
            Grazie alla convenzione tra Dritto e Serenis, puoi iniziare un percorso di psicoterapia individuale a una tariffa agevolata.
          </LBTLabel>

          <LBTSpacer spacing={4} isFixed />
          <LBTButton
            datatestid="home_button_serenis"
            variant="contained"
            color="primary-negative"
            size="medium"
            onClick={() => navigate(`/${APP_ROUTES.SERENIS}`)}
          >
            Ottieni il tuo codice sconto
          </LBTButton>
          <LBTSpacer spacing={6} />
        </Section>
      )}

      {weltis && (
        <Section backgroundColor={getBgColor()}>
          <LBTSpacer spacing={4} />
          <LBTLabel variant="delaDisplay" component="h3">
            Pianifica la tua pensione e le finanze con Weltis
          </LBTLabel>
          <LBTSpacer spacing={4} isFixed />
          <LBTLabel variant="spGroteskSubtitle">
            Esperti finanziari certificati e indipendenti dalle banche, al tuo fianco. Una prima sessione gratuita per capire da dove partire.
          </LBTLabel>
          <LBTImage image="financialLiteracy1" />
          <LBTSpacer spacing={1} isFixed />
          <LBTButton
            datatestid="home_button_weltis"
            variant="contained"
            color="primary-negative"
            size="medium"
            onClick={() => navigate(`/${APP_ROUTES.WELTIS}`)}
          >
            Prenota la tua sessione qui
          </LBTButton>
          <LBTSpacer spacing={6} />
        </Section>
      )}

      {bsolutionsweb && (
        <Section backgroundColor={getBgColor()}>
          <LBTSpacer spacing={4} />
          <LBTLabel variant="delaDisplay" component="h3">
            Il CAF, senza la coda
          </LBTLabel>
          <LBTSpacer spacing={4} isFixed />
          <LBTLabel variant="spGroteskSubtitle">
           BSolutions porta online servizi CAF, Patronato e assistenza fiscale, con professionisti veri e pratiche gestibili da casa.
          </LBTLabel>
          <LBTSpacer spacing={2} />
          <LBTImage image="workCall3" />
          <LBTSpacer spacing={4} isFixed />
          <LBTLabel variant="spGroteskSubtitle">
            Grazie alla partnership con Dritto, hai accesso a una scontistica dedicata su diversi servizi BSolutions.
          </LBTLabel>
          <LBTSpacer spacing={4} isFixed />
          <LBTButton
            datatestid="home_button_bsolutionsweb"
            variant="contained"
            color='primary-negative'
            size="medium"
            onClick={handleBSolutionsWebSSOClick}
          >
            Scopri gli sconti
          </LBTButton>
          <LBTSpacer spacing={6} />
        </Section>
      )}

      {tfr && (
        <Section backgroundColor={getBgColor()}>
          <LBTSpacer spacing={4} />
          <LBTLabel variant="delaDisplay" component="h3">
            Cosa fare con il TFR? E la pensione integrativa serve?
          </LBTLabel>
          <LBTSpacer spacing={4} isFixed />
          <LBTLabel variant="spGroteskSubtitle">
            Per fare la scelta giusta è importante conoscere tutte le opzioni.
            Ti aiutiamo a pianificare il tuo futuro previdenziale, finanziario e
            assicurativo
          </LBTLabel>
          <LBTSpacer spacing={2} />
          <LBTImage image="pigAtBeach" />
          <LBTSpacer spacing={4} isFixed />
          <LBTLabel variant="bodyText">
            Prenota una sessione online di 60 minuti con gli esperti di CiaoElsa
            per fare tutte le domande che vuoi e ricevere informazioni specifiche
            sul tuo caso
          </LBTLabel>

          <LBTSpacer spacing={4} isFixed />
          <LBTButton
            datatestid="home_button_tfr"
            variant="contained"
            color="primary-negative"
            size="medium"
            onClick={() => navigate(`/${APP_ROUTES.SEVERANCE_PAY}`)}
          >
            Prenota una sessione a 99€
          </LBTButton>
          <LBTSpacer spacing={1} isFixed />
          <LBTLabel variant="sourceCaption">invece che 149€</LBTLabel>
          <LBTSpacer spacing={2} isFixed />
          <LBTChip
            size="medium"
            variant="filled"
            color="lemonCake"
            label={
              <>
                codice sconto: <BoldSpan>LBTSPECIAL50</BoldSpan>
              </>
            }
            sx={{ padding: '4px 8px' }}
          />
          <LBTSpacer spacing={4} isFixed />
          <Box display="flex" alignItems="center">
            <LBTLabel
              variant="sourceCaption"
              component="small"
              textAlign="center"
            >
              servizio offerto da:
            </LBTLabel>
            <Box maxHeight="16px">
              <Image src={ElsaLogo} />
            </Box>
          </Box>
          <LBTSpacer spacing={4} isFixed />
        </Section>
      )}

      <Modal
        open={!currentUser.first_login}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        lang="it"
        translate="no"
      >
        <Paper
          sx={{
            width: isDesktop ? '489px' : '90%',
            borderRadius: '24px',
          }}
        >
          <Stack direction="column" spacing="24px" padding="16px">
            <LBTLabel
              sx={{ marginRight: '1px' }}
              textAlign="left"
              variant="miscTitleSource"
            >
              È il tuo primo accesso, grazie per la fiducia!
            </LBTLabel>

            <LBTLabel textAlign="left" variant="miscDescription">
              Prima di lanciarti nell'esplorazione un'informazione importante:
              con il primo accesso abbiamo creato il tuo account. Lo
              cancelleremo nel caso in cui l'azienda sospenda il servizio oppure
              nel momento in cui non sarai più suo dipendente
            </LBTLabel>

            {terms_and_conditions && (
              <Stack direction="row" alignItems="center">
                <LBTCheckbox
                  sx={{ marginLeft: '-9px' }}
                  checked={isContractAccepted}
                  datatestid="home_checkbox_contract_acceptance"
                  onChange={val => setIsContractAccepted(!!val)}
                />
                <LBTLabel
                  textAlign="left"
                  variant="sourceCaption"
                  sx={{ lineHeight: '24px' }}
                >
                  Dichiaro di aver letto e accettato i{' '}
                  <a
                    href={CONTRACT_PDF_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    termini e condizioni di utilizzo della piattaforma
                  </a>
                </LBTLabel>
              </Stack>
            )}

            <Stack direction="row" spacing="16px" justifyContent="flex-end">
              <LBTButton
                datatestid="home_button_first_access_submit"
                variant="contained"
                size="medium"
                disabled={!!terms_and_conditions && !isContractAccepted}
                onClick={closeFirstVisitModal}
              >
                Ho capito
              </LBTButton>
            </Stack>
          </Stack>
        </Paper>
      </Modal>
    </>
  );
};

export default Home;
